import { memo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BedFilter from 'components/FilterFields/BedFilter';
import BathFilter from 'components/FilterFields/BathFilter';
import CarSpacesFilter from 'components/FilterFields/CarSpacesFilter';
import PriceFilter from 'components/FilterFields/PriceFilter';
import RangeSelectArea from 'components/FilterFields/RangeSelectArea';
import FloorAreaFilter from 'components/FilterFields/FloorAreaFilter';
import AdvancedDateFilter from 'components/FilterFields/AdvancedDateFilter';
import YearBuiltFilter from 'components/FilterFields/YearBuiltFilter';
import SwitchFilter from 'components/FilterFields/SwitchFilter';
import ActiveListingsFilter from 'components/FilterFields/ActiveListingsFilter';
import TypeFilter from 'components/FilterFields/TypeFilter';
import SearchableCheckboxSelect from 'components/FilterFields/SearchableCheckboxSelect';
import KeywordsFilter from 'components/FilterFields/KeywordsFilter';
import PropertyTypes from 'constants/propertyTypes';
import ListingTypes from 'constants/listingTypes';
import { LABELS, SALES, LISTINGS, RENTALS } from 'constants/comparableTypes';
import {
    CHECKBOX_FILTERS,
    SALES_CLASSIFICATION_OPTIONS,
} from 'constants/filterOptions';
import useFilterStore from 'zustandStore/useFilterStore';
import { isNZLocale } from 'lib/PropertyUtils';
import SelectFilter from 'components/FilterFields/SelectFilter';
import AdvancedRadiusFilters from 'components/FilterFields/AdvancedRadiusFilters';

function CoreFilters(props) {
    const propertySubTypeOptions = useFilterStore(state => state.propertySubTypeOptions);
    const landUseOptions = useFilterStore(state => state.landUseOptions);
    const devZoneOptions = useFilterStore(state => state.devZoneOptions);

    const onViewAll = (type) => {
        props.handleSlidingPanel(true);
        props.setActiveSlot(type);
    };

    return (
        <Box
            sx={{
                mb: 2,
                width: '100%',
            }}
        >
            <Typography
                variant="text4"
                gutterBottom
                sx={{
                    mb: 2.5,
                    display: 'block',
                }}
            >
                Core Filters
            </Typography>
            <Grid container spacing={2}>
                <Grid item size={4}>
                    <BedFilter comparableType={props.comparableType} isDropdown={false}/>
                </Grid>
                <Grid item size={4}>
                    <BathFilter comparableType={props.comparableType} isDropdown={false}/>
                </Grid>
                <Grid item size={4}>
                    <CarSpacesFilter comparableType={props.comparableType} isDropdown={false}/>
                </Grid>
            </Grid>
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <Grid container spacing={2}>
              <Grid item size={6}>
                  <RangeSelectArea
                      comparableType={props.comparableType}
                      label="Land Size"
                      attrName="landArea"
                      sx={{
                          width: '100%',
                      }}
                  />
              </Grid>
              <Grid item size={6}>
                  <FloorAreaFilter comparableType={props.comparableType} />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item size={6}>
                    <YearBuiltFilter
                        comparableType={props.comparableType}
                        attrName="yearBuilt"
                        label="Year Built"
                    />
                </Grid>
            </Grid>
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <Grid container spacing={2} mb={2}>
                <Grid item size={6}>
                    <PriceFilter
                        label={`${LABELS[props.comparableType]} Price`}
                        attrName="price"
                        subLabel="Select an option or manually enter a value"
                        comparableType={props.comparableType}
                        isDropdown={false}
                        hasRentalFrequencyTabs={props.comparableType === RENTALS}
                    />
                </Grid>
                <Grid container item size={6}>
                    <Grid item size={2} />
                    <Grid item size={10}>
                        <AdvancedDateFilter
                            header={`${LABELS[props.comparableType]} Date`}
                            comparableType={props.comparableType}
                            label="Select Range"
                            attr="date"
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={2} mb={2}>
                {
                    props.comparableType === SALES && isNZLocale() && (
                        <Grid item size={6}>
                            <SelectFilter
                                title="Sales Index"
                                subTitle="Select an option or manually enter a value"
                                comparableType={SALES}
                                attrName="salesClassification"
                                options={SALES_CLASSIFICATION_OPTIONS}
                            />
                        </Grid>
                    )
                }
                {
                    props.comparableType === SALES && (
                        <Grid container item size={6}>
                            {
                                props.comparableType === SALES && isNZLocale() && (
                                    <Grid item size={2} />
                                )
                            }
                            <Grid item size={10}>
                                <AdvancedDateFilter
                                    header="Settlement Date"
                                    comparableType={props.comparableType}
                                    label="Select Range"
                                    attr="settlementDate"
                                />
                            </Grid>
                        </Grid>
                    )
                }
                {
                    props.comparableType === LISTINGS && (
                        <>
                            <Grid item size={6}>
                                <ActiveListingsFilter comparableType={props.comparableType} />
                            </Grid>
                            <Grid container item size={6}>
                                <Grid item size={2} />
                                <Grid item size={10}>
                                    <AdvancedDateFilter
                                        header="First Listing Date"
                                        comparableType={props.comparableType}
                                        label="Select Range"
                                        attr="firstListingDate"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )
                }

            </Grid>
            {
                props.comparableType === SALES && (
                    <Grid container spacing={2} mb={2}>
                        <Grid item size={6}>
                            <SwitchFilter
                                attr="excludeRecentlyAdvisedSales"
                                label="Exclude recently advised sales"
                                comparableType={props.comparableType}
                            />
                        </Grid>
                        <Grid item size={6}>
                            <SwitchFilter
                                attr="excludeRelatedParties"
                                label="Exclude related parties"
                                comparableType={props.comparableType}
                            />
                        </Grid>
                    </Grid>
                )
            }
            {
                props.comparableType === RENTALS && (
                    <Grid container spacing={2} mb={2}>
                        <Grid item size={6}>
                            <ActiveListingsFilter comparableType={props.comparableType} />
                        </Grid>
                        <Grid container item size={6}>
                            <Grid item size={2} />
                            <Grid item size={10}>
                                <AdvancedDateFilter
                                    header="First Rental Date"
                                    comparableType={props.comparableType}
                                    label="Select Range"
                                    attr="rentalLastCampaignStartDate"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )
            }
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            {
                props.comparableType === LISTINGS && (
                    <>
                        <TypeFilter
                            comparableType={props.comparableType}
                            isDropdown={false}
                            columnSize={4}
                            types={ListingTypes}
                            attr="listingType"
                            label="Listing Type"
                        />
                        <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
                    </>
                )
            }
            <TypeFilter
                comparableType={props.comparableType}
                isDropdown={false}
                columnSize={4}
                types={PropertyTypes}
                attr="propertyType"
            />
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <SearchableCheckboxSelect
                label={CHECKBOX_FILTERS.propertySubType.label}
                attr={CHECKBOX_FILTERS.propertySubType.key}
                comparableType={props.comparableType}
                isDropdown={false}
                options={propertySubTypeOptions}
                onViewAll={() => onViewAll(CHECKBOX_FILTERS.propertySubType.key)}
                placeholder={CHECKBOX_FILTERS.propertySubType.placeholder}
            />
            {!isNZLocale() && (
                <>
                    <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
                    <KeywordsFilter comparableType={props.comparableType} />
                </>
            )}
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <AdvancedRadiusFilters comparableType={props.comparableType} />
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <SearchableCheckboxSelect
                label={CHECKBOX_FILTERS.devZone.label}
                attr={CHECKBOX_FILTERS.devZone.key}
                comparableType={props.comparableType}
                isDropdown={false}
                options={devZoneOptions}
                onViewAll={() => onViewAll(CHECKBOX_FILTERS.devZone.key)}
                placeholder={CHECKBOX_FILTERS.devZone.placeholder}
            />
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
            <SearchableCheckboxSelect
                label={CHECKBOX_FILTERS.landUse.label}
                attr={CHECKBOX_FILTERS.landUse.key}
                comparableType={props.comparableType}
                isDropdown={false}
                options={landUseOptions}
                onViewAll={() => onViewAll(CHECKBOX_FILTERS.landUse.key)}
                placeholder={CHECKBOX_FILTERS.landUse.placeholder}
            />
            <Divider orientation="horizontal" sx={{mt: 3, mb: 3}}/>
        </Box>
    );
}

CoreFilters.propTypes = {
    comparableType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    handleSlidingPanel: PropTypes.func.isRequired,
    setActiveSlot: PropTypes.func.isRequired,
};

export default memo(CoreFilters);